import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import CountUp from "react-countup";
import {round, roundWithComma} from "app/utils/stringUtil";
import {CountUpUtil} from "app/utils/countUpUtil";
import React, {useEffect, useState} from "react";
import {ResponseResultCode} from "app/model/response-api";
import NoDataToDisplay from "highcharts/modules/no-data-to-display";
import DashboardLoading from "./DashboardLoading";
import {useAppSelector} from "app/store/hook";
import DashboardNoData from "./DashboardNoData";
import {toast} from "react-toastify";
import {DbUserService} from "app/services/db/dbUserService";
import {makeCompareUpDownBadge} from "./EnergySplyStatus";
import {DashboardModuleProps} from "app/model/db/dashboardModuleProps";
import DashboardCard from "./DashboardCard";
NoDataToDisplay(Highcharts);

const graphB: any = {
  chart: {
    height: 270,
    spacingTop: 0,
    spacingBottom: 0,
    spacingLeft: 0,
    spacingRight: 0,
  },
  colors: ["#FD6868", "#FFBB24"],
  title: {text: ""},
  exporting: {enabled: false},
  credits: {enabled: false},
  legend: {
    layout: 'horizontal',
    verticalAlign: 'top',
    borderWidth: 0,
    symbolHeight: 10,
    symbolPadding: 5,
    itemDistance: 20,
    itemMarginBottom: 10,
    labelFormat: `<span style="font-size: 0.75rem;">{name}</span>`,
  },
  subtitle: {
    align: 'center',
    verticalAlign: 'middle',
    y: 35,
    useHTML: true,
  },
  tooltip: {
    formatter: function (this: any) {
      return `<span style="color: ${this.color};">●&nbsp;</span>${this.point.name}: <strong style="font-size: 0.75rem;">${roundWithComma(this.point.y, 2)}</strong>kWh (${round(this.point.percentage)}%)`;
    },
    style: {
      fontSize: '0.75rem'
    }
  },
  plotOptions: {
    pie: {
      allowPointSelect: true,
      cursor: 'pointer',
      innerSize: "72%",
      borderRadius: 0,
    },
    series: {
      showInLegend: true,
      borderWidth: 0,
      dataLabels: {
        enabled: false,
      },
    },
  },
  series: [
    {
      name: '수요 전력',
      type: 'pie',
      data: [],
    },
  ],
};

interface PwrConsumeModel {
  flxUse: number;
  noFlxUse: number;
  totUse: number;
  flxUseRt: number;
  noFlxUseRt: number;
}

export default function EnergyConsumStatus(props: Readonly<DashboardModuleProps>) {
  const hasFacility = useAppSelector((state) => state.mgResource.hasFacility);
  const [chartB, setChartB] = useState<any>();
  const [consumeData, setConsumeData] = useState<PwrConsumeModel>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    DbUserService.getInstance().fetchPowerConsumeInfo().then(response => {
      if (response && response.resultCode == ResponseResultCode.Y && response.data) {
        const flxUse = response.data.flxUse ?? 0;
        const noFlxUse = response.data.norUse ?? 0;
        const flxUsePrev = response.data.flxUsePrev ?? 0;
        const noFlxUsePrev = response.data.norUsePrev ?? 0;
        setConsumeData({
          flxUse: flxUse,
          noFlxUse: noFlxUse,
          totUse: flxUse + noFlxUse,
          flxUseRt: flxUse !== 0 && flxUsePrev === 0 ? 100 : Math.round((flxUse - flxUsePrev) / flxUsePrev * 100),
          noFlxUseRt: noFlxUse !== 0 && noFlxUsePrev === 0 ? 100 : Math.round((noFlxUse - noFlxUsePrev) / noFlxUsePrev * 100),
        });
      } else {
        toast.error('데이터를 불러오지 못했습니다.. 다시 시도해 주시기 바랍니다.');
      }
    }).catch(() => {
      toast.error('시스템 오류가 발생했습니다. 다시 시도해 주시기 바랍니다.');
    }).finally(() => {
      setIsLoading(false);
    });
  }, [])

  useEffect(() => {
    createChart();
    const handleResize = () => {createChart()};
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [consumeData]);

  const createChart = () => {
    let seriesData: any[] = [];
    let subtitleB = {};
    let borderW = 0;

    if (consumeData) {
      seriesData = [
        {
          name: '일반 부하',
          y: consumeData.noFlxUse,
        }, {
          name: '유연 부하',
          y: consumeData.flxUse,
        }
      ];

      subtitleB = {
        align: 'center',
        verticalAlign: 'middle',
        y: 35,
        useHTML: true,
        text: `
          <div style="text-align: center;">
            <span style="color: #333; font-size: 1.2rem; font-weight: 800;">${roundWithComma(consumeData?.totUse ?? 0, 2)}</span>
            <br/>
            <span style="color: #555; font-size: 0.75rem; font-weight: 500;">kWh</span>
          </div>
        `,
      }
      borderW = (consumeData.noFlxUse === 0 && consumeData.flxUse === 0) ? 0.5 : 0;
    }

    setChartB({
      ...graphB,
      subtitle: subtitleB,
      series: [
        {
          type: 'pie',
          name: '소비 전력',
          data: seriesData,
          borderWidth:borderW,
        },
      ]
    });
  }

  return (
    <DashboardCard
      title="에너지 소비 현황"
      isConfigMode={props.isConfigMode ?? false}
      moreButtonVisible={true}
      moreButtonUrl="/pc/eu/supplyconsumption"
      dashboardConfig={props.dashboardConfig}
      mgMdmsType={"KM"}
    >
      <DashboardLoading isLoading={isLoading} />
      {/*{!isLoading && !hasFacility ? (*/}
      {/*  <DashboardNoData message="등록된 부하 설비가 없습니다." />*/}
      {/*) : (*/}
        <div className="dash-box-flex">
          <div className="chart">
            {chartB && (
              <HighchartsReact highcharts={Highcharts} options={chartB}/>
            )}
          </div>
          <div className="flex-col2">
            <div className="box-style11 pd0">
              <div className="box-in">
                <span>총 소비 전력</span>
                <span>
                  <strong>
                    <CountUp
                      end={round(consumeData?.totUse ?? 0, 2)}
                      decimals={CountUpUtil.getDecimal(round(consumeData?.totUse ?? 0, 2))}
                      duration={0.5}
                    />
                  </strong>
                  <em>kWh</em>
                </span>
              </div>
            </div>
            <span>금일 전력 소비량</span>
            <div className="list">
              <span>일반 부하</span>
              <span>
                {makeCompareUpDownBadge(consumeData?.noFlxUseRt)}
                <strong>{roundWithComma(consumeData?.noFlxUse ?? 0, 2)}</strong>
                  <em>kWh</em>
                </span>
            </div>
            <div className="list">
              <span>유연 부하</span>
              <span>
                {makeCompareUpDownBadge(consumeData?.flxUseRt)}
                <strong>{roundWithComma(consumeData?.flxUse ?? 0, 2)}</strong>
                  <em>kWh</em>
                </span>
            </div>
          </div>
        </div>
      {/*)}*/}
    </DashboardCard>
  );
}